import { resolvePortalMessage, type PortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `Organization` → Finance → Allowances and caps — `/finance/allowances` (plan-014 §4, design §27.15.1). Owned by plan-014
 * unit **E3b** (FR12-ENT-02, FR12-ENT-04, FR12-ENT-06, FR12-ENT-09; AC12-14).
 *
 * Drawn to the prototype's Finance page (`design/WeTalk.dc.html`, `data-screen-label="Organization"`, `allow`): kicker
 * "<organization> · <month>", "Allowances and spend caps", the lede ("Included talk time is counted in minutes, not
 * euros. A cap limits what a workspace may spend; a warning only tells you."), the Finance tab strip, then —
 *
 *   stats      Trial minutes ("0 of 30 left"), Included minutes ("—, none on pay as you go"), Paid this month,
 *              Unallocated ("—, applies to monthly plans") — from E3a's `GET …/entitlements`
 *   notice     "Order of use": trial, then included, then paid; the same second never twice; text tests free
 *   notice     the organization's concurrency entitlement as admission enforces it, and where it comes from
 *   table      "Per workspace": Allocation, Spent, Cap, At the cap, Warn at — "Edit caps" opens the cap form for a
 *              workspace the person may change (`spend_cap.write` through their role IN it), sent with the cap's
 *              revision (`0` creates it)
 *   form       "Allocate included minutes" — only while a shared plan grants talk time (none at launch: pay as you go),
 *              for `account.finance.manage`, with the live unallocated remainder
 *
 * WHO SEES WHAT: the page reads with `account.finance.read` (Owner, Admin, Finance); anyone else is told who can. What
 * a role holds decides what the page OFFERS; the API decides. Every refusal is worded from its stable code
 * (`./allowance-copy.ts`), a stale revision re-reads.
 *
 * `?workspace=<id>` opens the cap form for that workspace (Workspace detail links here).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { holdsWorkspaceCapability, type MemberRole } from "@wetalk/domain";
import { Button, Field, Icon, Input, Select } from "@wetalk/ui";
import { useState, type FormEvent, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import type { ConsoleGateway } from "../../../api/gateway.js";
import { useGateway } from "../../../api/provider.js";
import type { EntitlementGrantView, EntitlementOverviewView, EntitlementWorkspaceView } from "../../../api/slice/entitlement.js";
import type { WorkspaceCardView } from "../../../api/slice/workspace-management.js";
import { GLYPH } from "../../../shell/glyph.js";
import { roleHolds, useOpenAccount } from "../../account-admin/overview/account-overview-screen.js";
import { Block, BlockNote, FormPanel, Notice, StatGrid, type Outcome } from "../../account-admin/page/organization-page.js";
import {
  COVERAGE_SEQUENCE_NOTICE,
  allocationTextOf,
  atCapTextOf,
  capTextOf,
  concurrencySourceTextOf,
  entitlementRefusalTextOf,
  eurText,
  includedStatOf,
  minuteText,
  organizationConcurrencyText,
  paidStatOf,
  talkGrantsAt,
  trialStatOf,
  unallocatedStatOf,
  warnAtTextOf,
} from "./allowance-copy.js";
import { SpendCapForm } from "./spend-cap-form.js";
import { talkTimeText } from "../../../talk-time.js";

import "./allowance.css";
import { FinanceTabs } from "../finance-tabs.js";

/** The page's search parameters: `workspace` — whose cap the form opens on. */
export interface AllowanceSearch {
  readonly workspace?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * The route's `validateSearch`: a total function over the untrusted address bar, so a hand-edited URL
 * still opens a valid page.
 */
export function allowanceSearchOf(raw: Readonly<Record<string, unknown>>): AllowanceSearch {
  const workspace = raw["workspace"];
  return typeof workspace === "string" && UUID.test(workspace) ? { workspace } : {};
}

export interface AllowanceScreenProps {
  readonly search: AllowanceSearch;
  readonly onSearch: (next: AllowanceSearch) => void;
}

/** The query key every allowance read shares (Workspace detail invalidates it after a cap change). */
export const entitlementKey = (accountId: string) => ["entitlements", accountId] as const;


/** Every workspace card the person may see (each carries their own role in it), all pages. */
async function everyWorkspaceCard(gateway: ConsoleGateway): Promise<readonly WorkspaceCardView[]> {
  const all: WorkspaceCardView[] = [];
  let cursor: string | null = null;
  do {
    const page = await gateway.readWorkspaceCards({ q: null, cursor });
    all.push(...page.workspace);
    cursor = page.nextCursor;
  } while (cursor !== null);
  return all;
}

/** Whether the person's role IN a workspace changes its cap — `spend_cap.write` is never held through the organization. */
export function mayChangeCap(role: MemberRole | null): boolean {
  return role !== null && holdsWorkspaceCapability(role, "spend_cap.write");
}

/** One row of "Per workspace", in the prototype's columns. */
export interface AllowanceRow {
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly allocation: string;
  readonly spent: string;
  readonly cap: string;
  readonly atCap: string;
  readonly warnAt: string;
  readonly mayChange: boolean;
}

export function allowanceRowsOf(overview: EntitlementOverviewView, cards: readonly WorkspaceCardView[], currency: string): readonly AllowanceRow[] {
  const talk = talkGrantsAt(overview.grant, new Date(overview.at));
  return overview.workspace.map((one) => ({
    workspaceId: one.workspaceId,
    workspaceName: one.workspaceName,
    allocation: allocationTextOf(one.workspaceId, talk),
    spent: eurText(one.monthChargedMinor, one.spendCap === null ? currency : one.spendCap.currency),
    cap: capTextOf(one.spendCap),
    atCap: atCapTextOf(one.spendCap),
    warnAt: warnAtTextOf(one.spendCap),
    mayChange: mayChangeCap(cards.find((card) => card.workspaceId === one.workspaceId)?.memberRole ?? null),
  }));
}

// ── the page frame ───────────────────────────────────────────────────────────────────────────────────────

function AllowancePage(props: { readonly outcome: Outcome | null; readonly children?: ReactNode }): ReactNode {
  usePortalTranslation("finance");
  return (
    <section className="op-page">
      <h1 className="op-title">{portalText("finance", "m0377")}</h1>
      <p className="op-lede">{portalText("finance", "m0378")}</p>
      <FinanceTabs current="allowances" />
      {props.outcome === null ? null : (
        <p className={props.outcome.tone === "done" ? "op-toast" : "op-toast op-toast-problem"} role="status">
          <Icon glyph={props.outcome.tone === "done" ? GLYPH.checkCircle : GLYPH.warningCircle} size="lg" />
          <span>{resolvePortalMessage(props.outcome.text)}</span>
        </p>
      )}
      {props.children}
    </section>
  );
}

// ── the page ─────────────────────────────────────────────────────────────────────────────────────────────

export function AllowanceScreen(props: AllowanceScreenProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const open = useOpenAccount();
  const accountId = open.accountId;
  const canRead = roleHolds(open.accountRole, "account.finance.read");
  const canManage = roleHolds(open.accountRole, "account.finance.manage");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [editing, setEditing] = useState(props.search.workspace !== undefined);

  const overview = useQuery({
    queryKey: entitlementKey(accountId ?? "none"),
    queryFn: () => gateway.readEntitlements(accountId ?? ""),
    enabled: accountId !== null && canRead,
  });
  const cards = useQuery({
    queryKey: ["workspace-cards", "allowances"],
    queryFn: () => everyWorkspaceCard(gateway),
    enabled: accountId !== null && canRead,
  });

  const reread = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["entitlements"] });
  };

  if (open.loading || (canRead && accountId !== null && overview.isPending)) {
    return (
      <AllowancePage outcome={null}>
        <BlockNote>{portalText("finance", "m0379")}</BlockNote>
      </AllowancePage>
    );
  }
  if (accountId === null || !canRead) {
    return (
      <AllowancePage outcome={null}>
        <BlockNote>{portalText("finance", "m0380")}</BlockNote>
      </AllowancePage>
    );
  }
  if (overview.isError || overview.data === undefined) {
    return (
      <AllowancePage outcome={null}>
        <div className="al-actions">
          <BlockNote>{apiProblemText(overview.error, portalText("finance", "m0381"))}</BlockNote>
          <Button variant="secondary" onClick={() => void overview.refetch()}>{portalText("finance", "m0382")}</Button>
        </div>
      </AllowancePage>
    );
  }

  const data = overview.data;
  const at = new Date(data.at);
  const currency = data.balance[0]?.currency ?? data.workspace.find((one) => one.spendCap !== null)?.spendCap?.currency ?? "EUR";
  const talk = talkGrantsAt(data.grant, at);
  const rows = allowanceRowsOf(data, cards.data ?? [], currency);
  const editable = rows.filter((one) => one.mayChange);
  return (
    <AllowancePage outcome={outcome}>
      <Block label={portalText("finance", "m0383")}>
        <StatGrid stat={[trialStatOf(data.balance), includedStatOf(talk), paidStatOf(data.workspace, currency), unallocatedStatOf(talk)]} />
      </Block>
      <Block>
        <Notice tone="accent" glyph={GLYPH.stack} title={COVERAGE_SEQUENCE_NOTICE.title} text={COVERAGE_SEQUENCE_NOTICE.text} />
      </Block>
      <Block>
        <Notice
          tone="neutral"
          glyph={GLYPH.usersThree}
          title={portalText("finance", "m0384", { value1: String(data.concurrency.entitlement) })}
          text={`${organizationConcurrencyText(data.concurrency.entitlement)}. ${concurrencySourceTextOf(data.concurrency.source)}`}
        />
      </Block>
      <Block
        title={portalText("finance", "m0385")}
        dek={portalText("finance", "m0386")}
        buttons={
          editable.length > 0 && !editing ? (
            <Button variant="secondary" onClick={() => setEditing(true)}>{portalText("finance", "m0387")}</Button>
          ) : undefined
        }
      >
        {rows.length === 0 ? (
          <BlockNote>{portalText("finance", "m0388")}</BlockNote>
        ) : (
          <div className="op-table-wrap">
            <table className="table op-table">
              <thead>
                <tr>
                  <th>{portalText("finance", "m0389")}</th>
                  <th className="al-right">{portalText("finance", "m0390")}</th>
                  <th className="al-right">{portalText("finance", "m0391")}</th>
                  <th className="al-right">{portalText("finance", "m0392")}</th>
                  <th>{portalText("finance", "m0393")}</th>
                  <th>{portalText("finance", "m0394")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.workspaceId}>
                    <td className="al-nowrap">
                      <span className="op-cell-person">{row.workspaceName}</span>
                    </td>
                    <td className={row.allocation === "—" ? "op-cell al-right al-nowrap al-dim" : "op-cell al-right al-nowrap"}>{row.allocation}</td>
                    <td className="op-cell al-right al-nowrap">{row.spent}</td>
                    <td className={row.cap === "No cap" ? "op-cell al-right al-nowrap al-dim" : "op-cell al-right al-nowrap"}>{row.cap}</td>
                    <td className="op-cell">{row.atCap}</td>
                    <td className="op-cell al-nowrap">{row.warnAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cards.isError ? <BlockNote>{apiProblemText(cards.error, portalText("finance", "m0395"))}</BlockNote> : null}
        {editing && editable.length > 0 ? (
          <CapEditor
            rows={editable}
            workspace={data.workspace}
            preferred={props.search.workspace ?? null}
            onPick={(workspaceId) => props.onSearch({ workspace: workspaceId })}
            onStale={reread}
            onDone={(text) => {
              setEditing(false);
              props.onSearch({});
              if (text !== null) setOutcome({ tone: "done", text });
              reread();
            }}
          />
        ) : null}
        {editing && editable.length === 0 && cards.isSuccess ? (
          <BlockNote>{portalText("finance", "m0396")}</BlockNote>
        ) : null}
      </Block>
      {canManage
        ? talk.map((grant) => (
            <AllocationBlock key={grant.entitlementGrantId} accountId={accountId} grant={grant} workspace={data.workspace} onStale={reread} onOutcome={setOutcome} />
          ))
        : null}
    </AllowancePage>
  );
}

// ── "Edit caps" ─────────────────────────────────────────────────────────────────────────────────────────────

function CapEditor(props: {
  readonly rows: readonly AllowanceRow[];
  readonly workspace: readonly EntitlementWorkspaceView[];
  readonly preferred: string | null;
  readonly onPick: (workspaceId: string) => void;
  readonly onStale: () => void;
  readonly onDone: (text: PortalMessage | null) => void;
}): ReactNode {
  usePortalTranslation("finance");
  const first = props.rows.find((one) => one.workspaceId === props.preferred) ?? props.rows[0];
  const [workspaceId, setWorkspaceId] = useState(first?.workspaceId ?? "");
  const row = props.rows.find((one) => one.workspaceId === workspaceId) ?? first;
  if (row === undefined) return null;
  const found = props.workspace.find((one) => one.workspaceId === row.workspaceId);
  const current = found === undefined ? null : found.spendCap;
  return (
    <div aria-label={portalText("finance", "m0397")}>
      {props.rows.length > 1 ? (
        <div className="al-pick">
          <Field label={portalText("finance", "m0389")}>
            {(control) => (
              <Select
                {...control}
                value={row.workspaceId}
                onChange={(event) => {
                  setWorkspaceId(event.target.value);
                  props.onPick(event.target.value);
                }}
              >
                {props.rows.map((one) => (
                  <option key={one.workspaceId} value={one.workspaceId}>
                    {one.workspaceName}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      ) : null}
      <SpendCapForm
        key={`${row.workspaceId}-${current === null ? 0 : current.revision}`}
        workspaceId={row.workspaceId}
        workspaceName={row.workspaceName}
        current={current}
        onStale={props.onStale}
        onCancel={() => props.onDone(null)}
        onSaved={() => props.onDone(() => portalText("finance", "m0398", { value1: row.workspaceName }))}
      />
    </div>
  );
}

// ── "Allocate included minutes" ─────────────────────────────────────────────────────────────────────────────

/** Minutes typed → seconds, or `null` for anything but a whole number of minutes. */
export function allocationSecondOf(text: string): number | null {
  const trimmed = text.trim().replace(/\s*min$/, "");
  if (trimmed === "") return 0;
  if (!/^\d{1,9}$/.test(trimmed)) return null;
  return Number(trimmed) * 60;
}

function AllocationBlock(props: {
  readonly accountId: string;
  readonly grant: EntitlementGrantView;
  readonly workspace: readonly EntitlementWorkspaceView[];
  readonly onStale: () => void;
  readonly onOutcome: (outcome: Outcome) => void;
}): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const { grant } = props;
  const allocatedOf = (workspaceId: string): number => grant.allocation.find((one) => one.workspaceId === workspaceId)?.allocatedUnit ?? 0;
  const [typed, setTyped] = useState<Readonly<Record<string, string>>>(() =>
    Object.fromEntries(props.workspace.map((one) => [one.workspaceId, allocatedOf(one.workspaceId) === 0 ? "" : String(Math.floor(allocatedOf(one.workspaceId) / 60))])),
  );
  const second = props.workspace.map((one) => ({ workspaceId: one.workspaceId, second: allocationSecondOf(typed[one.workspaceId] ?? "") }));
  const invalid = second.some((one) => one.second === null);
  const total = second.reduce((sum, one) => sum + (one.second ?? 0), 0);
  const left = grant.grantedUnit - total;
  const sent = second.filter((one) => (one.second ?? 0) > 0 || grant.allocation.some((each) => each.workspaceId === one.workspaceId));

  const saving = useMutation({
    mutationFn: () =>
      gateway.changeEntitlementAllocations(
        props.accountId,
        { entitlementGrantId: grant.entitlementGrantId, allocation: sent.map((one) => ({ workspaceId: one.workspaceId, allocatedUnit: one.second ?? 0 })) },
        grant.allocationRevision,
      ),
    onSuccess: (changed) => {
      props.onOutcome({ tone: "done", text: () => (portalText("finance", "m0399", { value1: minuteText(changed.unallocatedUnit) })) });
      props.onStale();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "revision_stale") props.onStale();
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    saving.mutate();
  };
  return (
    <Block title={portalText("finance", "m0400")} dek={portalText("finance", "m0401", { value1: minuteText(grant.grantedUnit) })}>
      <FormPanel onSubmit={submit} label={portalText("finance", "m0400")}>
        {props.workspace.map((one) => {
          const used = grant.allocation.find((each) => each.workspaceId === one.workspaceId);
          const usedSecond = used === undefined ? 0 : used.consumedUnit + used.reservedUnit;
          const value = typed[one.workspaceId] ?? "";
          return (
            <div key={one.workspaceId} className="al-alloc-row">
              <span className="al-alloc-name">{one.workspaceName}</span>
              <Field
                label={portalText("finance", "m0402", { value1: one.workspaceName })}
                {...(usedSecond > 0 ? { description: portalText("finance", "m0403", { value1: talkTimeText(usedSecond) }) } : {})}
                {...(allocationSecondOf(value) === null ? { error: portalText("finance", "m0404") } : {})}
              >
                {(control) => (
                  <Input
                    {...control}
                    value={value}
                    inputMode="numeric"
                    placeholder="0"
                    onChange={(event) => {
                      const next = event.currentTarget.value;
                      setTyped((before) => ({ ...before, [one.workspaceId]: next }));
                    }}
                  />
                )}
              </Field>
            </div>
          );
        })}
        <p className="al-remainder" role="status">
          {left >= 0 ? portalText("finance", "m0405", { value1: minuteText(left) }) : portalText("finance", "m0406", { value1: minuteText(-left) })}
        </p>
        {saving.isError ? (
          <p className="al-error" role="alert">
            {entitlementRefusalTextOf(saving.error, "allocation")}
          </p>
        ) : null}
        <div className="al-actions">
          <Button variant="primary" type="submit" disabled={invalid || left < 0 || sent.length === 0 || saving.isPending}>{portalText("finance", "m0407")}</Button>
        </div>
      </FormPanel>
    </Block>
  );
}
